import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Sparkles } from "lucide-react";
import Logo from "@/assets/woba.svg";
import { PLATFORM_STATS } from "../constants";
import type { AIModeState, RankedSpace, SpaceAdjustment } from "../types";
import { formatUTCDate } from "../../proposal-standard/utils";
import { groupRoomsIntoCombos, sumComboTotals } from "../utils/comboGrouping";

const CARD_WIDTH = 148;
const CARD_GAP = 8;

interface ProposalPreviewProps {
  state: AIModeState;
}

export default function ProposalPreview({ state }: ProposalPreviewProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const { diagnosis, location, adjustments } = state;
  const selectedSpaceIds = location?.selectedSpaceIds ?? [];
  const spacePool = adjustments?.selectedSpaces?.length
    ? adjustments.selectedSpaces
    : (location?.rankedSpaces ?? []);
  const selectedSpaces = spacePool.filter((s) =>
    selectedSpaceIds.includes(s.id),
  );

  const recommendedSpaceIds = adjustments?.recommendedSpaceIds ?? [];
  const recommendedSpaces = spacePool.filter((s) =>
    recommendedSpaceIds.includes(s.id),
  );

  const companyName = diagnosis?.companyName ?? "Cliente";
  const city =
    diagnosis?.targetCity ||
    location?.location?.label?.split(",")[0] ||
    "Brasil";

  const validityDate = adjustments?.proposalValidity
    ? formatUTCDate(adjustments.proposalValidity)
    : "—";

  const positionsLabel = (() => {
    const min =
      location?.capacityMin ??
      (diagnosis?.positionsMin !== "" ? diagnosis?.positionsMin : null);
    const max =
      location?.capacityMax ??
      (diagnosis?.positionsMax !== "" ? diagnosis?.positionsMax : null);
    if (min != null && max != null) return `${min} – ${max}`;
    if (min != null) return `${min}+`;
    if (max != null) return `até ${max}`;
    return null;
  })();

  const displayStats =
    positionsLabel != null
      ? [
          PLATFORM_STATS[0],
          { value: positionsLabel, label: "Posições" },
          ...PLATFORM_STATS.slice(2),
        ]
      : PLATFORM_STATS;

  function getAdj(spaceId: string): SpaceAdjustment | undefined {
    return adjustments?.spaceAdjustments?.find((a) => a.spaceId === spaceId);
  }

  function getPricePerPos(space: RankedSpace): number {
    const adj = getAdj(space.id);
    if (adj && space.capacity > 0)
      return Math.round(adj.totalPrice / space.capacity);
    return space.pricePerWorkstation;
  }

  // Client-facing monthly price for a room (matches the client page total).
  function getClientPrice(space: RankedSpace): number {
    return getAdj(space.id)?.clientPrice ?? space.monthlyPrice ?? 0;
  }

  // One room line (used both standalone and inside a combo card).
  const renderRoomRow = (space: RankedSpace) => (
    <div key={space.id} className="flex items-start gap-1.5">
      <MapPin
        className="w-3 h-3 flex-shrink-0 mt-px"
        style={{ color: "#FFD900" }}
      />
      <div className="min-w-0">
        <p
          className="text-[10px] font-medium leading-tight break-words"
          style={{ color: "#1A1814" }}
        >
          {space.roomName}
        </p>
        <p className="text-[9px] break-words" style={{ color: "#9E9A94" }}>
          {space.spaceName}
        </p>
        {space.address && (
          <p
            className="text-[9px] mt-0.5 break-words"
            style={{ color: "#B8B4AE" }}
          >
            {space.address.split(" - ").slice(1).join(" - ") || space.address}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="w-full h-full overflow-y-auto ai-wizard-scroll relative"
      style={{
        background:
          "linear-gradient(160deg, #f8f7f6 0%, #f0eeeb 40%, #ebe8e4 100%)",
        animation: "proposalComplete 0.55s cubic-bezier(0.16,1,0.3,1) both",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(255,217,0,0.07) 0%, transparent 60%)",
          zIndex: 0,
        }}
      />

      <div
        className="relative flex items-center justify-center min-h-full py-8 px-6"
        style={{ zIndex: 1 }}
      >
        <div
          className="rounded-2xl overflow-hidden flex-shrink-0 relative w-full"
          style={{
            maxWidth: selectedSpaces.length > 8 ? 680 : 360,
            boxShadow:
              "0 1px 2px rgba(26,24,20,0.04), 0 4px 16px rgba(26,24,20,0.08)",
            background: "#fff",
            border: "1px solid #E8E6E3",
            animation: "float 5s ease-in-out 0.6s infinite",
          }}
        >
          <div
            className="px-5 pt-5 pb-4 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #FFD900 0%, #FFE94D 50%, #FFF3A3 100%)",
            }}
          >
            <div
              className="absolute inset-y-0 pointer-events-none"
              style={{
                width: "30%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)",
                animation: "shimmerSlide 2.8s ease-in-out 1s infinite",
              }}
            />
            <div className="flex items-center justify-between mb-4">
              <img src={Logo} alt="Woba" className="h-5 w-auto" />
              <span
                className="text-[8px] font-bold tracking-widest uppercase"
                style={{ color: "rgba(26,24,20,0.5)" }}
              >
                Proposta Comercial
              </span>
            </div>

            <p
              className="text-sm font-bold leading-snug mb-1"
              style={{ color: "#1A1814" }}
            >
              A forma mais inteligente de contratar escritórios
            </p>

            <p
              className="text-[11px] mb-3"
              style={{ color: "rgba(26,24,20,0.6)" }}
            >
              {companyName} · {city}
            </p>

            <div className="flex justify-end">
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                style={{
                  background: "#fff",
                  color: "#1A1814",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                }}
              >
                <MapPin className="w-3 h-3" style={{ color: "#22C55E" }} />
                {city}
              </span>
            </div>
          </div>

          <div
            className="grid grid-cols-4 divide-x divide-[#F0EEEB]"
            style={{ borderBottom: "1px solid #F0EEEB", background: "#FDFBF7" }}
          >
            {displayStats.map((s, i) => (
              <div key={s.label} className="px-2 py-3 text-center">
                <div
                  className="text-[11px] font-bold inline-block"
                  style={{
                    color: "#1A1814",
                    animation: `statPop 3.2s ease-in-out ${i * 0.8}s infinite`,
                  }}
                >
                  {s.value}
                </div>
                <div className="text-[8px]" style={{ color: "#9E9A94" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div
            className="px-4 py-3"
            style={{ borderBottom: "1px solid #F0EEEB" }}
          >
            <p
              className="text-[8px] font-bold uppercase tracking-wider mb-2"
              style={{ color: "#9E9A94" }}
            >
              Salas Selecionadas
            </p>
            {selectedSpaces.length === 0 ? (
              <p className="text-[10px]" style={{ color: "#C8C6C3" }}>
                Nenhum espaço selecionado
              </p>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "6px" }}
              >
                {groupRoomsIntoCombos(selectedSpaces).map((unit) => {
                  if (unit.kind === "single") return renderRoomRow(unit.space);
                  const { totalCapacity, totalPrice } = sumComboTotals(
                    unit.spaces,
                    getClientPrice,
                  );
                  return (
                    <div
                      key={unit.comboId}
                      style={{
                        border: "1px solid #C9DAF0",
                        background: "#F4F8FD",
                        borderRadius: "8px",
                        padding: "6px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <p
                        className="text-[9px] font-bold leading-none"
                        style={{ color: "#3B6FB5", letterSpacing: "0.02em" }}
                      >
                        Conjunto · {totalCapacity} pos · R${" "}
                        {totalPrice.toLocaleString("pt-BR")}/mês
                      </p>
                      {unit.spaces.map((space) => (
                        <div key={`${unit.comboId}:${space.id}`}>
                          {renderRoomRow(space)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {recommendedSpaces.length > 0 && (
            <div
              className="px-4 py-3"
              style={{ borderBottom: "1px solid #F0EEEB" }}
            >
              <div className="flex items-center justify-between mb-2">
                <p
                  className="text-[8px] font-bold uppercase tracking-wider"
                  style={{ color: "#9E9A94" }}
                >
                  Recomendados
                </p>
                {recommendedSpaces.length > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        const next = Math.max(carouselIndex - 1, 0);
                        carouselRef.current?.scrollTo({
                          left: next * (CARD_WIDTH + CARD_GAP),
                          behavior: "smooth",
                        });
                        setCarouselIndex(next);
                      }}
                      disabled={carouselIndex === 0}
                      className="flex items-center justify-center rounded-full transition-opacity"
                      style={{
                        width: 16,
                        height: 16,
                        background: "#F0EEEB",
                        opacity: carouselIndex === 0 ? 0.35 : 1,
                      }}
                    >
                      <ChevronLeft
                        className="w-2.5 h-2.5"
                        style={{ color: "#43403D" }}
                      />
                    </button>
                    <span
                      className="text-[8px] font-semibold tabular-nums"
                      style={{
                        color: "#B8B4AE",
                        minWidth: 24,
                        textAlign: "center",
                      }}
                    >
                      {carouselIndex + 1}/{recommendedSpaces.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const next = Math.min(
                          carouselIndex + 1,
                          recommendedSpaces.length - 1,
                        );
                        carouselRef.current?.scrollTo({
                          left: next * (CARD_WIDTH + CARD_GAP),
                          behavior: "smooth",
                        });
                        setCarouselIndex(next);
                      }}
                      disabled={carouselIndex === recommendedSpaces.length - 1}
                      className="flex items-center justify-center rounded-full transition-opacity"
                      style={{
                        width: 16,
                        height: 16,
                        background: "#F0EEEB",
                        opacity:
                          carouselIndex === recommendedSpaces.length - 1
                            ? 0.35
                            : 1,
                      }}
                    >
                      <ChevronRight
                        className="w-2.5 h-2.5"
                        style={{ color: "#43403D" }}
                      />
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <div
                  ref={carouselRef}
                  className="flex gap-2 overflow-x-auto pb-1"
                  style={{
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                  }}
                >
                  {recommendedSpaces.map((space) => {
                    const adj = getAdj(space.id);
                    const pricePerPos = getPricePerPos(space);
                    const credits = adj?.totalCredits ?? 0;
                    return (
                      <div
                        key={space.id}
                        className="rounded-xl p-3 relative overflow-hidden flex-shrink-0"
                        style={{
                          background: "#1A1814",
                          width: recommendedSpaces.length === 1 ? "100%" : 148,
                          scrollSnapAlign: "start",
                        }}
                      >
                        <div
                          className="absolute inset-0 rounded-xl pointer-events-none"
                          style={{
                            background:
                              "radial-gradient(ellipse at 50% 0%, rgba(255,217,0,0.12), transparent 70%)",
                            animation: "configGlow 2.8s ease-in-out infinite",
                          }}
                        />
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold"
                            style={{ background: "#FFD900", color: "#1A1814" }}
                          >
                            <Sparkles className="w-2 h-2" />
                            Recomendado
                          </span>
                        </div>
                        <p
                          className="text-[10px] font-semibold mb-0.5 leading-snug"
                          style={{ color: "#FDFBF7" }}
                        >
                          {space.roomName || space.spaceName}
                        </p>
                        {space.spaceName &&
                          space.roomName !== space.spaceName && (
                            <p
                              className="text-[9px] mb-0.5"
                              style={{ color: "#9E9A94" }}
                            >
                              {space.spaceName}
                            </p>
                          )}
                        <p
                          className="text-[9px] mb-2"
                          style={{ color: "#9E9A94" }}
                        >
                          {space.capacity ? `${space.capacity} Estações` : ""}
                          {space.capacity && space.sizeM2 ? " · " : ""}
                          {space.sizeM2 ? `${space.sizeM2} m²` : ""}
                        </p>
                        {pricePerPos > 0 && (
                          <p
                            className="text-sm font-bold mb-2"
                            style={{ color: "#FFD900" }}
                          >
                            R$ {pricePerPos.toLocaleString("pt-BR")}
                            <span
                              className="text-[9px] font-normal ml-1"
                              style={{ color: "#9E9A94" }}
                            >
                              /posição
                            </span>
                          </p>
                        )}
                        <div className="flex gap-4">
                          {credits > 0 && (
                            <div>
                              <p
                                className="text-[7px] font-semibold uppercase tracking-wider"
                                style={{ color: "#78736D" }}
                              >
                                Créditos
                              </p>
                              <p
                                className="text-[9px] font-medium"
                                style={{ color: "#FDFBF7" }}
                              >
                                {credits}/mês
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div
            className="px-4 py-3 flex items-center justify-end"
            style={{ background: "#FDFBF7" }}
          >
            <span className="text-[9px]" style={{ color: "#9E9A94" }}>
              Válida até {validityDate}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
